/**
 * Per-tenant phone-approval sign-in policy — Zod schemas.
 *
 * Mirrors the backend wire shapes on
 * /api/v1/tenants/{slug}/signin-policy. Four knobs:
 *
 *   - pureid_qr_channel_enabled — PureID's own QR channel,
 *                                governs BOTH sign-in (Phase F)
 *                                AND enrollment Card 2 (Phase G).
 *   - webauthn_hybrid_disabled — master switch for the
 *                                WebAuthn hybrid transport
 *                                (caBLE / OS-passkey-picker
 *                                "Use another device").
 *   - number_matching          — anti-relay step on the QR
 *                                channel.
 *   - match_number_length      — 2 or 4 digits. 4 is the Phase G
 *                                default for new tenants.
 *
 * Defaults (matching `signinpolicy.DefaultPolicy`): qr ON,
 * hybrid AVAILABLE, number_matching=user_choice, length=4. The
 * server materialises these on a missing row; legacy tenants
 * whose row predates Phase G stay on length=2 until they save.
 */

import { z } from "zod";
import { signinPolicySchema } from "./api.generated";

export const numberMatchingSchema = z.enum([
  "disabled",
  "user_choice",
  "always",
]);
export type NumberMatching = z.infer<typeof numberMatchingSchema>;

/** What the admin console calls each number-match setting. */
export const NUMBER_MATCHING_LABELS: Record<NumberMatching, string> = {
  disabled: "Off",
  user_choice: "Optional",
  always: "Required",
};

// Push-based login fallback (WebAuthn-hostile clients). Device scope
// constrains where a push approval may complete relative to the login
// device. Mirrors signinpolicy.PushLoginDeviceScope.
export const pushLoginDeviceScopeSchema = z.enum([
  "same_device_only",
  "cross_device_allowed",
  "both",
]);
export type PushLoginDeviceScope = z.infer<typeof pushLoginDeviceScopeSchema>;

// 2-or-4 enum at the schema layer so the admin UI leans on the
// type rather than free-form numbers — a "3" typed by a future
// dev never reaches the wire.
export const matchNumberLengthSchema = z
  .number()
  .int()
  .refine((n): n is 2 | 4 => n === 2 || n === 4, {
    message: "match_number_length must be 2 or 4",
  });
export type MatchNumberLength = 2 | 4;

// The contract bounds match_number_length to 2..4 and the server stores only 2 or 4.
export function toMatchNumberLength(n: number): MatchNumberLength {
  return n === 2 ? 2 : 4;
}

// The response is defined by the API contract (SignInPolicy) and generated.
export { signinPolicySchema };
export type SignInPolicy = z.infer<typeof signinPolicySchema>;
